import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { setShowSuccessModal, setCurrentStep } from '../../../store/slices/bookSlice';
import { endpoints } from '../../../api/api';

const LABELS = ['Terrible', 'Poor', 'Okay', 'Good', 'Excellent'];

const SuccessModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const showSuccessModal = useAppSelector((state) => state.book.showSuccessModal);
  const bookId = useAppSelector((state) => state.book.bookId);

  const [step, setStep] = useState<'feedback' | 'success'>('feedback');
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!showSuccessModal) return null;

  const handleGoToDashboard = () => {
    dispatch(setShowSuccessModal(false));
    dispatch(setCurrentStep('book-info'));
    navigate('/');
  };

  const handleFeedbackSubmit = async () => {
    if (!rating) { setStep('success'); return; }
    setSubmitting(true);
    try {
      await axios.post(endpoints.submitFeedback, {
        rating,
        message,
        type: 'post_submission',
        bookId: bookId ?? undefined,
      });
    } catch {
      // silently ignore — don't block the user
    } finally {
      setSubmitting(false);
      setStep('success');
    }
  };

  const active = hovered || rating;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl max-w-md w-full mx-4 shadow-lg border border-gray-200 overflow-hidden">

        {/* Feedback step */}
        {step === 'feedback' && (
          <div className="p-8">
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-green-500 text-2xl">✓</span>
              </div>
              <h2 className="text-gray-900 text-xl font-semibold">Submitted for Review!</h2>
              <p className="text-gray-500 text-sm mt-1">Our team will reach out within 48 hours.</p>
            </div>

            <div className="border-t border-gray-100 pt-5">
              <p className="text-gray-800 text-sm font-medium text-center mb-1">How was your experience?</p>
              <p className="text-gray-400 text-xs text-center mb-4">Takes 5 seconds — really helps us improve.</p>

              <div className="flex gap-2 justify-center mb-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHovered(star)}
                    onMouseLeave={() => setHovered(0)}
                    className="transition-transform hover:scale-110 active:scale-95"
                  >
                    <svg
                      className={`w-9 h-9 transition-colors ${star <= active ? 'text-orange-400' : 'text-gray-200'}`}
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  </button>
                ))}
              </div>
              <p className="text-center text-xs text-gray-400 h-4 mb-3">
                {active > 0 ? LABELS[active - 1] : ''}
              </p>

              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={2}
                placeholder="Anything you'd like to share? (optional)"
                className="w-full text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent placeholder-gray-300 mb-4"
              />

              <div className="flex gap-2">
                <button
                  onClick={() => setStep('success')}
                  className="flex-1 bg-white border border-gray-200 text-gray-500 text-sm font-medium py-2.5 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Skip
                </button>
                <button
                  onClick={handleFeedbackSubmit}
                  disabled={submitting}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium py-2.5 rounded-lg transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Sending...' : rating ? 'Submit' : 'Continue'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success step */}
        {step === 'success' && (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-green-500 text-3xl">✓</span>
            </div>
            <h2 className="text-gray-900 text-2xl font-semibold mb-4">Submitted for Review!</h2>
            <p className="text-gray-600 mb-6">
              Our team will reach out to you within 48 hours once the review is complete.
            </p>

            <div className="bg-orange-50 border border-orange-100 rounded-lg px-4 py-3 mb-6 text-left">
              <p className="text-orange-800 text-sm font-medium mb-1">Curious what you could earn?</p>
              <p className="text-orange-700 text-xs mb-2">While you wait, check how much royalty you'd make per book sold.</p>
              <a
                href="https://studio.indiepress.in/calculator"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-500 hover:text-orange-600 text-sm font-medium underline underline-offset-2"
              >
                Check the royalty calculator →
              </a>
            </div>

            <button
              onClick={handleGoToDashboard}
              className="w-full bg-gray-900 text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors font-medium"
            >
              Go Back to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuccessModal;